import Alpine from 'alpinejs'
import type { Ticket } from './interfaces'

export function normalizeTime(value: string): string | null {
    const text = value.trim()
    if (!text) return ''
    const match = text.match(/^(\d{1,2})(?::(\d{2}))?$/)
    const compact = text.match(/^(\d{1,2})(\d{2})$/)
    const parts = match ?? compact
    if (!parts) return null
    const hour = Number(parts[1])
    const minute = Number(parts[2] ?? 0)
    if (hour > 23 || minute > 59) return null
    return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
}

export default function registerTimePicker(): void {
    Alpine.data('bookingTimePicker', (ticket: Ticket) => ({
        ticket,
        draft: ticket.time || '',
        open: false,
        invalid: false,
        active: -1,
        popupStyle: {},
        get options(): string[] {
            return ['', ...[...ticket.times].sort()]
        },
        init() {
            this.$watch('ticket.time', (value: string) => {
                this.draft = value || ''
                this.invalid = false
            })
        },
        positionPopup() {
            const rect = this.$el.getBoundingClientRect()
            const scrollBox = this.$el.closest('.modal-scroll-box')?.getBoundingClientRect()
            const bottom = Math.min(window.innerHeight, scrollBox?.bottom ?? window.innerHeight)
            const top = Math.max(0, scrollBox?.top ?? 0)
            const below = bottom - rect.bottom - 16
            const above = rect.top - top - 16
            const upwards = below < 224 && above > below
            this.popupStyle = {
                top: upwards ? 'auto' : 'calc(100% + 6px)',
                bottom: upwards ? 'calc(100% + 6px)' : 'auto',
                maxHeight: `${Math.max(40, Math.min(224, upwards ? above : below))}px`,
            }
        },
        revealActive() {
            this.$nextTick(() => {
                const list = this.$refs.timeList
                const option = list?.querySelector<HTMLElement>(`[data-time-index="${this.active}"]`)
                if (!list || !option) return
                if (option.offsetTop < list.scrollTop) list.scrollTop = option.offsetTop
                else if (option.offsetTop + option.offsetHeight > list.scrollTop + list.clientHeight) {
                    list.scrollTop = option.offsetTop + option.offsetHeight - list.clientHeight
                }
            })
        },
        show() {
            if (this.open) return
            this.active = this.options.indexOf(normalizeTime(this.draft) ?? '')
            this.open = true
            this.positionPopup()
            this.revealActive()
        },
        input() {
            this.invalid = false
            this.open = true
            const normalized = normalizeTime(this.draft)
            this.active = this.draft ? this.options.findIndex(time => time === normalized || time.startsWith(this.draft)) : 0
            this.positionPopup()
            this.revealActive()
        },
        openNativePicker(input: HTMLInputElement) {
            if (typeof input.showPicker !== 'function') return
            try {
                input.showPicker()
            } catch {
                input.focus()
            }
        },
        choose(value: string) {
            ticket.time = value
            ticket.errors.time = false
            this.draft = value
            this.invalid = false
            this.open = false
            this.active = -1
        },
        commit() {
            const value = normalizeTime(this.draft)
            this.open = false
            this.active = -1
            this.invalid = value === null
            ticket.errors.time = this.invalid
            if (value !== null) this.choose(value)
        },
        keydown(event: KeyboardEvent) {
            if (event.key === 'Escape' && this.open) {
                event.preventDefault()
                event.stopPropagation()
                this.draft = ticket.time || ''
                this.invalid = false
                this.open = false
                this.active = -1
            } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault()
                if (!this.open) this.show()
                else this.active = Math.max(0, Math.min(this.options.length - 1, this.active + (event.key === 'ArrowDown' ? 1 : -1)))
                this.revealActive()
            } else if (event.key === 'Enter') {
                event.preventDefault()
                if (this.open && this.active >= 0) this.choose(this.options[this.active])
                else this.commit()
            }
        },
    }))
}
